import { HttpErrorResponse } from '@angular/common/http'
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	OnDestroy,
	OnInit,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { OrderTerms } from '@modules/orders-module/constants/order-terms'
import { OrderTypes } from '@modules/orders-module/constants/order-types'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderTermEnum } from '@modules/orders-module/enums/order-term.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { CreateOrderInterface } from '@modules/orders-module/interfaces/create-order.interface'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { AlpacaAccountInterface } from '@modules/alpaca-module/alpaca-account/interfaces/alpaca-account.interface'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { OrderTermSelectComponent } from '@shared/components/trading/orders/common/order-term-select/order-term-select.component'
import { OrderTypeSelectComponent } from '@shared/components/trading/orders/common/order-type-select/order-type-select.component'
import { SlTpType } from '@shared/components/trading/orders/common/sl-tp-controls/core/types/sl-tp.type'
import { TrailingStopControlsComponent } from '@shared/components/trading/orders/common/trailing-stop-controls/trailing-stop-controls.component'
import { TrailInterface } from '@modules/stocks-module/orders/interfaces/trail.interface'
import { TuiInputModule, TuiSelectModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy'
import { finalize, Subject, takeUntil } from 'rxjs'
import { SlTpInterface } from '@modules/stocks-module/orders/interfaces/sl-tp.interface'
import { SlTpEnum } from '@modules/stocks-module/orders/enums/sl-tp.enum'
import { SlTpControlsComponent } from '@shared/components/trading/orders/common/sl-tp-controls/sl-tp-controls.component'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PositionSideEnum } from '@modules/positions-module/enums/position-side.enum'
import { AlertService } from '@shared/services/alert.service'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { LiveTickerService } from '@modules/stocks-module/live-ticker/services/live-ticker.service'
import { PositionsStocksHelperService } from '@modules/stocks-module/positions/services/positions-stocks-helper.service'
import { StocksOrdersService } from '@modules/stocks-module/orders/services/stocks-orders.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { AlpacaAccountHelperService } from '@modules/alpaca-module/alpaca-account/services/alpaca-account-helper.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { OrderPriceInputComponent } from '../common/order-price-input/order-price-input.component'
import { TradeSideToggleComponent } from '../../common/trade-side-toggle/trade-side-toggle.component'
import { OrderQtyInputComponent } from '../common/order-qty-input/order-qty-input.component'
import { QtyTypeEnum } from '../common/order-qty-input/enums/qty-type.enum'
import { QtyTypeInterface } from '../common/order-qty-input/interfaces/qty-type.interface'
import { OrderBookComponent } from '@shared/components/trading/common/order-book/order-book.component'
import { AnimatedNumberDirective } from '@shared/directives/animated-number/animated-number.directive'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QtyTypes } from '../common/order-qty-input/constants/qty-types'

@UseLocales({ ru, en })
@Component({
	selector: 'app-create-order',
	templateUrl: './create-order.component.html',
	styleUrl: './create-order.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TuiSelectModule,
		TuiTextfieldControllerModule,
		ReactiveFormsModule,
		TuiInputModule,
		FormsModule,
		OrderTypeSelectComponent,
		OrderTermSelectComponent,
		OrderPriceInputComponent,
		OrderQtyInputComponent,
		SlTpControlsComponent,
		TrailingStopControlsComponent,
		TradeSideToggleComponent,
		TranslatePipe,
		ButtonComponent,
		PricePipe,
		OrderBookComponent,
		AnimatedNumberDirective,
	],
})
export class CreateOrderComponent implements OnInit, OnDestroy {
	protected readonly number: NumberConstructor = Number
	protected readonly math: Math = Math
	protected readonly qtyTypeEnum: typeof QtyTypeEnum = QtyTypeEnum
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderTermEnum: typeof OrderTermEnum = OrderTermEnum
	protected readonly positionSideEnum: typeof PositionSideEnum = PositionSideEnum
	protected readonly slTpEnum: typeof SlTpEnum = SlTpEnum
	protected readonly qtyTypes: QtyTypeInterface[] = QtyTypes
	protected readonly orderTypes: OrderTypeEnum[] = OrderTypes
	protected readonly orderTerms: OrderTermEnum[] = OrderTerms
	protected readonly trailingStopTerms: OrderTermEnum[] = [this.orderTermEnum.DAY, this.orderTermEnum.GTC]

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _formBuilder: FormBuilder = inject<FormBuilder>(FormBuilder)
	private readonly _alpacaAccountHelperService: AlpacaAccountHelperService =
		inject<AlpacaAccountHelperService>(AlpacaAccountHelperService)
	private readonly _ordersStocksService: StocksOrdersService = inject<StocksOrdersService>(StocksOrdersService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _liveTickerService: LiveTickerService = inject<LiveTickerService>(LiveTickerService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _positionsStocksHelperService: PositionsStocksHelperService =
		inject<PositionsStocksHelperService>(PositionsStocksHelperService)
	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)

	private _trail: TrailInterface = { trail_price: null, trail_percent: null }

	protected isStopLimitFilled: boolean = false
	protected allPositions: PositionsInterface[] = []

	protected targetPrice: WritableSignal<number> = signal(0)
	protected isLoadingCreate: WritableSignal<boolean> = signal(false)
	protected alpacaAccount: WritableSignal<AlpacaAccountInterface | null> = signal(null)
	protected position: WritableSignal<PositionsInterface | null> = signal(null)
	protected isLoadingAllPositions: WritableSignal<boolean> = signal(false)
	protected liveTicker: WritableSignal<LiveTickerInterface | null> = signal(null)
	protected slTpPrice: SlTpInterface<number> = {
		stopLoss: 0,
		takeProfit: 0,
	}
	protected isSlTpActive: WritableSignal<SlTpInterface<boolean>> = signal<SlTpInterface<boolean>>({
		stopLoss: false,
		takeProfit: false,
	})

	protected isVisibleSlTp: WritableSignal<boolean> = signal<boolean>(false)

	protected form: FormGroup | null = null

	protected priceMarket: Signal<number> = computed<number>((): number => {
		return this.form?.get('side')?.value === this.orderSideEnum.SELL ? this.askPrice() : this.bidPrice()
	})

	protected bidPrice: Signal<number> = computed<number>((): number => {
		return this.liveTicker()?.orderBook?.bid || 0
	})

	protected askPrice: Signal<number> = computed<number>((): number => {
		return this.liveTicker()?.orderBook?.ask || 0
	})

	get transactionAmount(): number {
		return this._getTransactionAmount()
	}

	get isActiveBuy(): boolean {
		return this.form?.get('side')?.value === this.orderSideEnum.BUY
	}

	get isVisibleStopPrice(): boolean {
		return this._ordersStocksHelperService.checkIsStopPriceVisible(this.form?.value?.type)
	}

	get isVisibleLimitPrice(): boolean {
		return this._ordersStocksHelperService.checkIsLimitPriceVisible(this.form?.value?.type)
	}

	get isVisibleTrailingStop(): boolean {
		return this._ordersStocksHelperService.checkIsTrailingStopVisible(this.form?.value?.type)
	}
	private _checkVisibleSlTp(): void {
		const formSide: OrderSideEnum | undefined = this.form?.get('side')?.value
		const positionSide: PositionSideEnum | undefined = this.position()?.side

		let isVisible: boolean = !positionSide

		if (positionSide === this.positionSideEnum.LONG && formSide === this.orderSideEnum.BUY) isVisible = true

		if (positionSide === this.positionSideEnum.SHORT && formSide === this.orderSideEnum.SELL) isVisible = true

		this.isVisibleSlTp.set(isVisible)
	}

	private _listenFormSide(): void {
		if (!this.form) return
		this.form
			.get('side')
			?.valueChanges.pipe(takeUntil(this._destroy$))
			.subscribe((): void => this._checkVisibleSlTp())
	}

	private _initPositionsParams(): void {
		this._positionsStocksHelperService.positionsParams$.next({
			page: 1,
			pageSize: 100,
		})
	}

	private _launchPositions(): void {
		this._tradingLauncherService
			.launchPositions$()
			.pipe(takeUntil(this._destroy$))
			.subscribe(([positions, loading]: [PositionsInterface[], boolean]): void => {
				this.allPositions = positions
				this.isLoadingAllPositions.set(loading)
				this._findPosition()
			})
	}

	private _findPosition(): void {
		const foundPosition: PositionsInterface | undefined = this.allPositions.find(
			(position: PositionsInterface): boolean => position?.symbol === this.liveTicker()?.ticker,
		)
		this.position.set(foundPosition ?? null)
		this._checkVisibleSlTp()
	}

	private _initForm(): void {
		this.form = this._formBuilder.group({
			stop_price: [0, [Validators.min(0)]],
			limit_price: [0, [Validators.min(0)]],
			type: [this.orderTypeEnum.MARKET],
			time_in_force: [this.orderTermEnum.GTC],
			qty: [0, [Validators.required, Validators.min(0)]],
			inputQty: [null, Validators.min(0)],
			extended_hours: [false],
			side: [this.orderSideEnum.BUY],
			activeQtyType: [this.qtyTypes[0]],
		})
	}

	private _listenAlpacaAccount(): void {
		this._alpacaAccountHelperService.alpacaAccount$
			.pipe(takeUntil(this._destroy$))
			.subscribe((alpacaAccount: AlpacaAccountInterface | null): void => {
				this.alpacaAccount.set(alpacaAccount)
			})
	}

	private _listenLiveTicker(): void {
		this._liveTickerService.liveTicker$
			.pipe(takeUntil(this._destroy$))
			.subscribe((liveTicker: LiveTickerInterface | null): void => {
				if (!liveTicker) {
					this._resetForm()
					this.liveTicker.set(null)
					return
				}

				if (this.liveTicker()?.ticker !== liveTicker.ticker) {
					this._resetForm()
					this.isStopLimitFilled = false
					this._updateTargetPrice()
				}

				this.liveTicker.set(liveTicker)
				this._findPosition()
				this._fillStopLimitPrices()
				this._calculateQty()
			})
	}

	private _getPriceByOrderType(): number {
		const formValue: CreateOrderInterface | undefined = this.form?.value
		if (!formValue) return this.priceMarket()

		switch (formValue.type) {
			case this.orderTypeEnum.MARKET:
				return this.priceMarket()
			case this.orderTypeEnum.STOP:
				return +(formValue.stop_price ?? 0)
			case this.orderTypeEnum.LIMIT:
				return +(formValue.limit_price ?? 0)
			case this.orderTypeEnum.STOP_LIMIT:
				return +(formValue.limit_price ?? 0)
			default:
				return this.priceMarket()
		}
	}

	private _getTransactionAmount(): number {
		const formValue: CreateOrderInterface | undefined = this.form?.value
		if (!formValue) return 0

		const qty: number = this.number(formValue.qty) || 0

		let stopPrice: number = 0
		let limitPrice: number = 0

		switch (formValue.type) {
			case this.orderTypeEnum.MARKET:
			case this.orderTypeEnum.TRAILING_STOP:
				return qty * (this.priceMarket() ?? 0)
			case this.orderTypeEnum.STOP:
				stopPrice = this.number(formValue.stop_price) || 0
				return stopPrice * qty
			case this.orderTypeEnum.LIMIT:
				limitPrice = this.number(formValue.limit_price) || 0
				return limitPrice * qty
			case this.orderTypeEnum.STOP_LIMIT:
				limitPrice = this.number(formValue.limit_price) || 0
				return limitPrice * qty
			default:
				return 0
		}
	}
	private _fillStopLimitPrices(): void {
		if (this.isStopLimitFilled || !this.form) return

		if (!this.askPrice()) return

		this.form.patchValue({
			stop_price: this.askPrice(),
			limit_price: this.askPrice(),
		})

		this.isStopLimitFilled = true
	}

	private _checkIsValidOrder(): boolean {
		const formValue: CreateOrderInterface | undefined = this.form?.value
		if (!formValue) return false

		const { active: slActive, priceValue: slPrice } = this.form?.get('stopLoss')?.value ?? 0
		const { active: tpActive, priceValue: tpPrice } = this.form?.get('takeProfit')?.value ?? 0
		const tpDiff: number = tpPrice - this.targetPrice()
		const slDiff: number = slPrice - this.targetPrice()
		const isLimitMaxQty: boolean = formValue.qty > (this.position()?.qty ?? 0)
		const isShortAndBuy: boolean =
			this.position()?.side === this.positionSideEnum.SHORT && formValue.side === this.orderSideEnum.BUY
		const isLongAndSell: boolean =
			this.position()?.side === this.positionSideEnum.LONG && formValue.side === this.orderSideEnum.SELL

		if (this.isVisibleStopPrice && +(formValue.stop_price ?? 0) <= 0) {
			this._alertService.error(this._translateService.instant('createOrder.errors.stopPriceZero'))
			return false
		}
		if (this.isVisibleLimitPrice && +(formValue.limit_price ?? 0) <= 0) {
			this._alertService.error(this._translateService.instant('createOrder.errors.limitPriceZero'))
			return false
		}
		if (this.isVisibleTrailingStop && !this._trail.trail_price && !this._trail.trail_percent) {
			this._alertService.error(this._translateService.instant('createOrder.errors.trailValueZero'))
			return false
		}

		if (formValue.qty <= 0) {
			this._alertService.error(this._translateService.instant('createOrder.errors.qtyZero'))
			return false
		}

		if ((isShortAndBuy || isLongAndSell) && isLimitMaxQty) {
			this._alertService.error(this._translateService.instant('createOrder.errors.maxQty'))
			return false
		}

		const isTpInvalid: boolean =
			this.form?.get('side')?.value === this.orderSideEnum.BUY ? tpDiff <= 0 : tpDiff >= 0
		if (tpActive && isTpInvalid)
			this._alertService.error(this._translateService.instant('createOrder.errors.invalidTakeProfit'))

		const isSlInvalid: boolean =
			(this.form?.get('side')?.value === this.orderSideEnum.BUY && slDiff >= 0) ||
			(this.form?.get('side')?.value === this.orderSideEnum.SELL && slDiff <= 0) ||
			slPrice < 0.0001
		if (slActive && isSlInvalid)
			this._alertService.error(this._translateService.instant('createOrder.errors.invalidStopLoss'))

		return true
	}

	private _getOrderForConfirm(): CreateOrderInterface | null {
		const formValue: CreateOrderInterface | undefined = this.form?.value
		if (!formValue) return null
		const isDayPlus: boolean = formValue.time_in_force === this.orderTermEnum.DAY_PLUS
		const { stopLoss, takeProfit }: SlTpInterface<number> = this.slTpPrice
		const { stopLoss: isStopLossActive, takeProfit: isTakeProfitActive }: SlTpInterface<boolean> =
			this.isSlTpActive()

		return {
			qty: formValue.qty,
			type: formValue.type,
			symbol: this.liveTicker()?.ticker || '',
			side: formValue.side,
			extended_hours: isDayPlus,
			time_in_force: isDayPlus ? this.orderTermEnum.DAY : formValue.time_in_force,
			...(this.isVisibleLimitPrice && { limit_price: formValue.limit_price }),
			...(this.isVisibleStopPrice && { stop_price: formValue.stop_price }),
			...(this.isVisibleTrailingStop && this._trail.trail_price && { trail_price: this._trail.trail_price }),
			...(this.isVisibleTrailingStop &&
				this._trail.trail_percent && { trail_percent: this._trail.trail_percent }),
			...(isStopLossActive && { stop_loss: { stop_price: stopLoss } }),
			...(isTakeProfitActive && { take_profit: { limit_price: takeProfit } }),
		}
	}

	private _calculateQty(): void {
		if (!this.form) return
		const inputQty: number = this.form.get('inputQty')?.value
		const value: number = +inputQty
		if (this.number.isNaN(value)) return

		let qty: number = value
		const equity: number = +(this.alpacaAccount()?.equity ?? 0)

		switch (this.form.get('activeQtyType')?.value.type) {
			case this.qtyTypeEnum.PERCENT:
				qty = (equity * value) / 100 / this._getPriceByOrderType()
				qty = this.math.floor(qty)
				break
			case this.qtyTypeEnum.DOLLAR:
				qty = value / this._getPriceByOrderType()
				qty = this.math.floor(qty)
				break
		}

		this.form.patchValue({ qty })
	}

	private _resetForm(): void {
		this.form?.patchValue({
			limit_price: 0,
			stop_price: 0,
			type: this.orderTypeEnum.MARKET,
			time_in_force: this.orderTermEnum.GTC,
			qty: 0,
			inputQty: null,
			extended_hours: false,
			side: this.orderSideEnum.BUY,
			activeQtyType: this.qtyTypes[0],
		})

		this.isStopLimitFilled = false
		this.isSlTpActive.set({ stopLoss: false, takeProfit: false })
		this.slTpPrice = { stopLoss: 0, takeProfit: 0 }
		this._trail = { trail_price: null, trail_percent: null }

		this._fillStopLimitPrices()
		this._updateTargetPrice()
	}

	private _onChangeOrderType(): void {
		const controlsToWatch: string[] = ['type', 'stop_price', 'limit_price']

		controlsToWatch.forEach((controlName: string): void => {
			this.form
				?.get(controlName)
				?.valueChanges.pipe(takeUntil(this._destroy$))
				.subscribe((): void => this._updateTargetPrice())
		})

		this.form
			?.get('type')
			?.valueChanges.pipe(takeUntil(this._destroy$))
			.subscribe((type: OrderTypeEnum): void => {
				if (type === this.orderTypeEnum.TRAILING_STOP) {
					this._trail = { trail_price: null, trail_percent: 1 }
					const currentTerm: OrderTermEnum = this.form?.get('time_in_force')?.value
					if (!this.trailingStopTerms.includes(currentTerm)) {
						this.form?.get('time_in_force')?.setValue(this.orderTermEnum.GTC, { emitEvent: false })
					}
				} else {
					this._trail = { trail_price: null, trail_percent: null }
				}
			})
	}

	private _updateTargetPrice(): void {
		const type: OrderTypeEnum = this.form?.get('type')?.value
		const stopPrice: number = this.form?.get('stop_price')?.value ?? 0
		const limitPrice: number = this.form?.get('limit_price')?.value ?? 0

		switch (type) {
			case this.orderTypeEnum.MARKET:
				this.targetPrice.set(this.priceMarket())
				break
			case this.orderTypeEnum.STOP:
				this.targetPrice.set(stopPrice)
				break
			default:
				this.targetPrice.set(limitPrice)
				break
		}
	}

	private _listenInputQty(): void {
		this.form
			?.get('inputQty')
			?.valueChanges.pipe(takeUntil(this._destroy$))
			.subscribe((): void => this._calculateQty())
	}

	protected createOrder(): void {
		if (!this._checkIsValidOrder()) return

		const order: CreateOrderInterface | null = this._getOrderForConfirm()
		if (!order) return

		this.isLoadingCreate.set(true)
		this._ordersStocksService
			.createOrder(order)
			.pipe(
				takeUntil(this._destroy$),
				finalize(() => this.isLoadingCreate.set(false)),
			)
			.subscribe(
				(): void => {
					this._alertService.success(
						this._translateService.instant('createOrder.errors.orderCreationSuccess', {
							symbol: order.symbol,
						}),
					)
					this._resetForm()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message ||
							this._translateService.instant('createOrder.errors.orderCreationFailed'),
					)
				},
			)
	}

	protected toggleTypeDeal(type: OrderSideEnum): void {
		this.form?.get('side')?.patchValue(type)
		this._updateTargetPrice()
	}

	protected setQtyType(qtyType: QtyTypeInterface): void {
		this.form?.get('activeQtyType')?.patchValue(qtyType)
		this.form?.get('inputQty')?.patchValue(0)
		this._calculateQty()
	}

	protected toggleActiveSlTp(type: SlTpType, value: boolean): void {
		this.isSlTpActive.set({
			...this.isSlTpActive(),
			[type === 'stop-loss' ? this.slTpEnum.STOP_LOSS : this.slTpEnum.TAKE_PROFIT]: value,
		})
	}

	protected onChangeTrail(trail: TrailInterface): void {
		this._trail = trail
	}

	protected togglePriceSlTp(type: SlTpType, value: number): void {
		this.slTpPrice = {
			...this.slTpPrice,
			[type === 'stop-loss' ? this.slTpEnum.STOP_LOSS : this.slTpEnum.TAKE_PROFIT]: value,
		}
	}

	ngOnInit(): void {
		this._listenAlpacaAccount()
		this._initPositionsParams()
		this._launchPositions()
		this._initForm()
		this._listenFormSide()
		this._listenInputQty()
		this._onChangeOrderType()
		this._listenLiveTicker()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
